import React from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { company, CONTACT_EMAIL } from '../../data/site';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate } from '../../utils/format';
import { calculateTotals, formatKr, lineTotal } from '../../utils/quote';
import type { QuoteLine } from '../../types/quote';
const ease = [0.23, 1, 0.32, 1] as const;
interface Props {
  quoteNumber: string;
  title: string;
  description: string;
  lines: QuoteLine[];
  quoteDate: string;
  validUntil: string;
  customerNotes: string;
  customer: {
    name: string;
    address: string;
    email: string;
    phone: string;
  };
  onClose: () => void;
}

/** Kundens visning af tilbuddet — samme indhold som en senere mailudsendelse. */
export function QuotePreview({
  quoteNumber,
  title,
  description,
  lines,
  quoteDate,
  validUntil,
  customerNotes,
  customer,
  onClose
}: Props) {
  const {
    settings
  } = useAdminData();
  const totals = calculateTotals(lines);
  return <div className="fixed inset-0 z-50 overflow-y-auto bg-ink/40 px-4 py-8" role="dialog" aria-modal="true" aria-labelledby="forhaandsvis-titel" data-id="element-225" data-mp="2u9sbnqbcm3tb">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24,
      ease
    }} className="mx-auto w-full max-w-3xl rounded-card bg-white p-6 md:p-10" data-id="element-226" data-mp="2kkh3q7077570">
        <div className="flex items-start justify-between gap-4" data-id="element-227" data-mp="2akzzlu0lgzd5">
          <img src={company.logoWide} alt={settings.companyName} className="h-10 w-auto" data-id="element-228" data-mp="2fkw6c10of4we" />
          <button type="button" onClick={onClose} aria-label="Luk forhåndsvisning" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-229" data-mp="21lczdbjlpzlo">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-230" data-mp="4tcs82a1mj6z" />
          </button>
        </div>

        <div className="mt-8 flex flex-wrap items-start justify-between gap-6" data-id="element-231" data-mp="2exwqcr8bu05">
          <div data-id="element-232" data-mp="3ui8l4myyj05q">
            <h2 id="forhaandsvis-titel" className="font-display text-2xl font-bold tracking-tight text-ink md:text-3xl" data-id="element-233" data-mp="3uhpalrbmze1q">
              Tilbud
            </h2>
            <p className="mt-1 text-[15px] text-ink-soft" data-id="element-234" data-mp="3kkkkjgeo6yfn">{quoteNumber}</p>
          </div>
          <dl className="text-[15px]" data-id="element-235" data-mp="m25iwwqm63os">
            <div className="flex justify-between gap-8" data-id="element-236" data-mp="m14189f3eiy3">
              <dt className="text-ink-muted" data-id="element-237" data-mp="h3khitzmldyr">Dato</dt>
              <dd className="text-ink" data-id="element-238" data-mp="h442kjkkoih0">{formatDate(quoteDate)}</dd>
            </div>
            <div className="mt-1 flex justify-between gap-8" data-id="element-239" data-mp="cbavvq5jn02a">
              <dt className="text-ink-muted" data-id="element-240" data-mp="7e107srk9s60">Gyldig til</dt>
              <dd className="text-ink" data-id="element-241" data-mp="7beve0w21qrh">{formatDate(validUntil)}</dd>
            </div>
          </dl>
        </div>

        <div className="mt-8 grid gap-6 border-y border-line py-6 sm:grid-cols-2" data-id="element-242" data-mp="14uq2fnkfawfx">
          <div data-id="element-243" data-mp="1003ll4vkobog">
            <p className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-244" data-mp="17dcltsgclpow">Til</p>
            <p className="mt-2 text-[15px] font-medium text-ink" data-id="element-245" data-mp="17ffn58myb58x">{customer.name || '—'}</p>
            <p className="text-[15px] text-ink-soft" data-id="element-246" data-mp="1cco4x846t79n">{customer.address}</p>
            <p className="text-[15px] text-ink-soft" data-id="element-247" data-mp="1hapnzt1m56l4">{customer.phone}</p>
            <p className="break-all text-[15px] text-ink-soft" data-id="element-248" data-mp="1mb50yjhzgc2k">{customer.email}</p>
          </div>
          <div className="sm:text-right" data-id="element-249" data-mp="1qcqm0s4o7wnu">
            <p className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-250" data-mp="1qbnrma853c36">Fra</p>
            <p className="mt-2 text-[15px] font-medium text-ink" data-id="element-251" data-mp="1v9z4yuxn84q5">{settings.companyName}</p>
            <p className="text-[15px] text-ink-soft" data-id="element-252" data-mp="2031o094snvs3">CVR: {settings.cvr}</p>
            <p className="text-[15px] text-ink-soft" data-id="element-253" data-mp="251mpsczp2nin">{settings.phone}</p>
            <p className="break-all text-[15px] text-ink-soft" data-id="element-254" data-mp="252xrm79kxl74">{CONTACT_EMAIL}</p>
          </div>
        </div>

        <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-ink" data-id="element-255" data-mp="2e1rntwj009k5">{title || 'Opgave'}</h3>
        {description && <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-ink-soft" data-id="element-256" data-mp="3bpmh67h6mn3q">{description}</p>}

        <table className="mt-6 w-full text-left text-[15px]" data-id="element-257" data-mp="36r1rh1zk3ltj">
          <thead data-id="element-258" data-mp="3kjhlxeee6biu">
            <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-259" data-mp="3khwsjs3tcymk">
              <th scope="col" className="py-2 font-medium" data-id="element-260" data-mp="3gifmoxkh5zgv">Beskrivelse</th>
              <th scope="col" className="py-2 font-medium" data-id="element-261" data-mp="2sra5adyvyz4g">Antal</th>
              <th scope="col" className="py-2 font-medium" data-id="element-262" data-mp="2stmxrl29srvr">Enhedspris</th>
              <th scope="col" className="py-2 text-right font-medium" data-id="element-263" data-mp="2nua34e55qgim">I alt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line" data-id="element-264" data-mp="31qdpjl9shkkj">
            {lines.map(line => <tr key={line.id} data-id="element-265" data-mp="2ws2fpajhva2b">
                <td className="py-2.5 text-ink" data-id="element-266" data-mp="h296f174znra">{line.description || '—'}</td>
                <td className="whitespace-nowrap py-2.5 text-ink-soft" data-id="element-267" data-mp="h4c6yf5x0za9">
                  {line.quantity} {line.unit}
                </td>
                <td className="whitespace-nowrap py-2.5 text-ink-soft" data-id="element-268" data-mp="kjngtwl6pl5q">{formatKr(line.unitPrice)}</td>
                <td className="whitespace-nowrap py-2.5 text-right font-medium text-ink" data-id="element-269" data-mp="oma4dlatojx4">
                  {formatKr(lineTotal(line))}
                </td>
              </tr>)}
            {lines.length === 0 && <tr data-id="element-270" data-mp="flvhihi7mwri">
                <td colSpan={4} className="py-6 text-center text-sm text-ink-muted" data-id="element-271" data-mp="flc51gbbx0kv">
                  Ingen linjer på tilbuddet endnu.
                </td>
              </tr>}
          </tbody>
        </table>

        <dl className="mt-6 ml-auto max-w-xs divide-y divide-line/70 text-[15px]" data-id="element-272" data-mp="tkv3fhec8xjz">
          <div className="flex justify-between gap-6 py-2" data-id="element-273" data-mp="tir0p2s6e4la">
            <dt className="text-ink-soft" data-id="element-274" data-mp="yiwog7vbr6y3">Subtotal ekskl. moms</dt>
            <dd className="font-medium text-ink" data-id="element-275" data-mp="1m9skjh6tcob5">{formatKr(totals.subtotal)}</dd>
          </div>
          <div className="flex justify-between gap-6 py-2" data-id="element-276" data-mp="1qzz7zdpla2rt">
            <dt className="text-ink-soft" data-id="element-277" data-mp="1r1k3p6nn3rr9">Moms</dt>
            <dd className="font-medium text-ink" data-id="element-278" data-mp="20wr994ead4m6">{formatKr(totals.vatAmount)}</dd>
          </div>
          <div className="flex justify-between gap-6 py-2" data-id="element-279" data-mp="1vwmtxhp46fxj">
            <dt className="font-medium text-ink" data-id="element-280" data-mp="1vv1v3fvroatv">Total inkl. moms</dt>
            <dd className="font-display text-lg font-bold text-ink" data-id="element-281" data-mp="1qwqqbjkbwqii">{formatKr(totals.total)}</dd>
          </div>
        </dl>

        {customerNotes && <div className="mt-8 rounded-xl bg-canvas p-5" data-id="element-282" data-mp="1hx3lsowaz4l8">
            <p className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-283" data-mp="1hyogqfmj4pjm">Vilkår og bemærkninger</p>
            <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-ink-soft" data-id="element-284" data-mp="1d5c4l4z1eb7r">
              {customerNotes}
            </p>
          </div>}
      </motion.div>
    </div>;
}